import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { RichTextEditor } from '@/components/announcements/rich-text-editor'
import { usePolls } from '@/hooks/use-polls'
import { useCreateResolution, useUpdateResolution } from '@/hooks/use-resolutions'
import { getApiErrorMessage } from '@/lib/api'
import { stripHtmlToText } from '@riverpark/utils/html'
import type { Resolution } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10)
}

export function ResolutionModal({
  resolution,
  prefill,
  onClose,
}: {
  /** Omit to create a new resolution instead of editing one. */
  resolution?: Resolution
  /** Only used in create mode — e.g. "Publish as resolution" from a closed poll's page. */
  prefill?: { title?: string; pollId?: string }
  onClose: () => void
}) {
  const create = useCreateResolution()
  const update = useUpdateResolution()
  const isEdit = !!resolution
  // Only closed polls make sense to attach a resolution to — an open one hasn't produced a
  // result yet. Cap the list generously; most estates won't run more than a handful at once.
  const { data: pollsData } = usePolls({ status: 'closed', limit: 100 })

  const [title, setTitle] = useState(resolution?.title ?? prefill?.title ?? '')
  const [body, setBody] = useState(resolution?.body ?? '')
  const [pollId, setPollId] = useState(resolution?.pollId ?? prefill?.pollId ?? '')
  const [passedAt, setPassedAt] = useState(
    resolution?.passedAt
      ? new Date(resolution.passedAt * 1000).toISOString().slice(0, 10)
      : todayInputValue(),
  )
  const [error, setError] = useState<string | null>(null)

  const pending = create.isPending || update.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (stripHtmlToText(body).length < 10) {
      setError('Body must be at least 10 characters')
      return
    }

    try {
      const input = {
        title,
        body,
        pollId: pollId || undefined,
        passedAt: new Date(`${passedAt}T00:00:00`).toISOString(),
      }
      if (isEdit) {
        await update.mutateAsync({ id: resolution.id, input })
      } else {
        await create.mutateAsync(input)
      }
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save resolution'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="resolution-modal-title" panelClassName="max-w-2xl">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="resolution-modal-title" className="font-serif text-xl text-ink">
            {isEdit ? 'Edit resolution' : 'Add resolution'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            minLength={5}
            maxLength={200}
          />
          <div className="space-y-1.5">
            <label htmlFor="resolution-body" className="font-sans text-sm font-medium text-ink">
              Body
            </label>
            <RichTextEditor
              id="resolution-body"
              value={body}
              onChange={setBody}
              allowImages={false}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="font-sans text-sm font-medium text-ink">From poll (optional)</label>
              <select
                className={SELECT_CLS}
                value={pollId}
                onChange={(e) => setPollId(e.target.value)}
              >
                <option value="">None — added directly</option>
                {pollsData?.data.map((poll) => (
                  <option key={poll.id} value={poll.id}>
                    {poll.title}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Passed on"
              type="date"
              value={passedAt}
              onChange={(e) => setPassedAt(e.target.value)}
              required
              hint="Backdate this when adding an older resolution."
            />
          </div>
          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={pending}>
            {isEdit ? 'Save changes' : 'Add resolution'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
